import type { ApiErrorCode, ApiErrorEnvelope } from '@/shared/types/api';

/**
 * Thin fetch wrapper for the Fidelen API.
 *
 * Responsibilities kept here on purpose:
 *   • Sanctum CSRF handshake and cookie credentials;
 *   • translation of the API error envelope into a typed exception;
 *   • locale and idempotency headers.
 *
 * It holds no business rules: the backend is the authority for every decision,
 * and the SPA only renders what it is told.
 */

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> & { fields?: Record<string, string[]> } = {},
    readonly requestId: string | null = null,
    /**
     * The API path that answered (`/platform/tenants`, `/me`…), so a refusal
     * can be traced to the session it concerns — an MFA step demanded by the
     * platform guard is not one the tenant session owes.
     */
    readonly path: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation messages, keyed by input name. */
  get fieldErrors(): Record<string, string[]> {
    return this.details.fields ?? {};
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED';
  }

  get isUnauthenticated(): boolean {
    return this.code === 'UNAUTHENTICATED';
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Nothing at this address for this reader: gone, mistyped, or not theirs to see. */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Query parameters. An array repeats its key once per value, under the key as
   * given — `{ 'types[]': ['a', 'b'] }` sends `types[]=a&types[]=b`.
   */
  query?: Record<
    string,
    string | number | boolean | null | undefined | readonly (string | number)[]
  >;
  /** Required by the API on duplicate-sensitive commands. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  locale?: string;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));

  return match ? decodeURIComponent(match[2]) : null;
}

let csrfReady: Promise<void> | null = null;

/**
 * Fetch the XSRF cookie once per session. Concurrent callers share the same
 * in-flight promise so a burst of mutations does not trigger a burst of
 * handshakes.
 */
export async function ensureCsrfCookie(): Promise<void> {
  if (readCookie('XSRF-TOKEN')) return;

  csrfReady ??= fetch('/sanctum/csrf-cookie', { credentials: 'include' })
    .then(() => undefined)
    .finally(() => {
      csrfReady = null;
    });

  await csrfReady;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      for (const item of value as readonly (string | number)[]) params.append(key, String(item));
      continue;
    }

    params.set(key, String(value));
  }

  const qs = params.toString();

  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const isMutation = method !== 'GET';

  if (isMutation) {
    await ensureCsrfCookie();
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  /*
   * Every request carries the language the reader is LOOKING AT.
   *
   * The backend resolves `status_label`, `next_action`, `current_holder` and
   * every display name server-side, so without this header it answers in the
   * user's STORED language. An English screen then renders Arabic status chips
   * — which is exactly what it did until this was wired. `apiDownload` already
   * documented the same hazard for exports; it applies to every read.
   *
   * `documentElement.lang` is the live value `applyDocumentLocale` sets, so the
   * client stays out of the i18n module and cannot drift from it.
   */
  const locale =
    options.locale ?? (typeof document !== 'undefined' ? document.documentElement.lang : undefined);

  if (locale) headers['X-Fidelen-Locale'] = locale;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  /*
   * A file upload (INV-01) sends FormData, and the browser must set
   * `Content-Type` itself — it has to append the multipart boundary, and a
   * hand-set header would produce a body the server cannot parse.
   */
  const isFormData = options.body instanceof FormData;

  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  const xsrf = readCookie('XSRF-TOKEN');
  if (isMutation && xsrf) headers['X-XSRF-TOKEN'] = xsrf;

  const response = await fetch(buildUrl(path, options.query), {
    method,
    credentials: 'include',
    headers,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope | null;

    if (envelope?.error) {
      throw new ApiError(
        envelope.error.code,
        envelope.error.message,
        response.status,
        envelope.error.details ?? {},
        envelope.error.request_id ?? null,
        path,
      );
    }

    // A non-enveloped failure means something outside the application answered
    // (a proxy, a network error). Surface it as a server error rather than
    // pretending it was a known domain outcome.
    throw new ApiError(
      'SERVER_ERROR',
      response.statusText || 'Request failed',
      response.status,
      {},
      null,
      path,
    );
  }

  return payload as T;
}

/**
 * RPT-11 — fetch an export and hand the bytes to the browser.
 *
 * Deliberately NOT a plain `<a href>`. The link would work — the API is
 * same-origin and cookie-authenticated — but a navigation cannot carry the
 * `X-Fidelen-Locale` header, so the file would come back in the reader's
 * STORED language rather than the one they are looking at. Somebody reading an
 * Arabic screen would download an English spreadsheet.
 *
 * A failure is still an API error envelope, so it is parsed and thrown like any
 * other: a download that silently saved a JSON error as `report.csv` would be
 * discovered by whoever opened it.
 */
export async function apiDownload(
  path: string,
  options: { query?: RequestOptions['query']; locale?: string; fallbackName: string } = {
    fallbackName: 'fidelen-export',
  },
): Promise<void> {
  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
  if (options.locale) headers['X-Fidelen-Locale'] = options.locale;

  const response = await fetch(buildUrl(path, options.query), {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const text = await response.text();
    const envelope = (text ? JSON.parse(text) : null) as ApiErrorEnvelope | null;

    if (envelope?.error) {
      throw new ApiError(
        envelope.error.code,
        envelope.error.message,
        response.status,
        envelope.error.details ?? {},
        envelope.error.request_id ?? null,
        path,
      );
    }

    throw new ApiError(
      'SERVER_ERROR',
      response.statusText || 'Download failed',
      response.status,
      {},
      null,
      path,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filenameFrom(response.headers.get('Content-Disposition')) ?? options.fallbackName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** The server's own filename, so the file is named the same wherever it lands. */
/**
 * The file name the server chose (OBS-010: client, report, scope, period, export date).
 *
 * RFC 6266: when the server sends both forms, the extended `filename*=UTF-8''…`
 * carries the Unicode name — an Arabic client's name — and wins over the ASCII
 * `filename=` fallback. Exported for its test.
 */
export function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;

  const extended = /filename\*=\s*UTF-8''([^;]+)/i.exec(disposition);

  if (extended) return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));

  const plain = /filename=\s*"?([^";]+)"?/i.exec(disposition);

  return plain ? decodeURIComponent(plain[1]) : null;
}

/** Generates the Idempotency-Key header value for a duplicate-sensitive command. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
